import React from 'react';
import { Calculator, Users, FileText, BarChart3, Shield, Smartphone, ArrowRight, Star } from 'lucide-react';
import { href } from 'react-router-dom';

const Products = () => {
    const products = [
        {
            name: 'Bancplus',
            category: 'Banking Management',
            icon: Users,
            description: 'The core banking solution from Bancplus is an integrated solution that automates all aspects of core banking operations across entities, languages and currencies. A part of the universal banking suite, Bancplusempowers financial institutions to introduce new products with ease and efficiently manage changes in existing ones.',
            features: ['Co-Operative Banks', 'Credit Co-Operative / Nidhi Companies', 'Branch less Banking',],
            price: 'Starting at 2000/month',
            rating: 4.9,
            users: '1,200+',
            image: 'https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=500&h=300&fit=crop',
            link: ''
        },
        {
            name: 'Smart Dairy',
            category: 'Dairy Management',
            icon: Calculator,
            description: 'Smart Dairy App is an advanced dairy management platform that helps businesses efficiently sell and manage milk quantity while providing detailed reports and analysis for every client. It streamlines milk tracking, ensures accurate quantity management, and offers insightful analytics to optimize operations.',
            features: [' Track & manage milk sales with precision and ease', 'Generate detailed reports for every client', 'Analyze data & insights to make smarter decisions'],
            price: 'Starting at 1500/month',
            rating: 4.8,
            users: '800+',
            image: 'https://nipl.in/wp-content/uploads/2025/02/sm.png',
            link: 'https://smartdairy.co.in/'
        },
        {
            name: 'Credix',
            category: 'Insurance',
            icon: BarChart3,
            description: 'Credix is a user-friendly insurance selling and earning app that empowers anyone to become an insurance seller. Customers can sign up, explore various insurance policies, and sell them to others while earning attractive monthly commissions. ',
            features: ['Sell insurance easily with a user-friendly app', 'Earn monthly commissions on every policy sold', 'Track earnings & manage policies in real time'],
            price: 'Starting at 3000/month',
            rating: 4.7,
            users: '600+',
            image: 'https://nipl.in/wp-content/uploads/2025/02/credix.png',
            link: 'http://admin.credix.life/'
        },
    ];

    return (
        <section id="products" className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Products</h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Discover our suite of specialized software solutions designed to transform how finance offices operate,
                        from client management to compliance and everything in between.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {products.map((product, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                            <div className="relative bg-green-950">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-48 object-cover"
                                />
                                <div className="absolute top-4 left-4">
                                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                        {product.category}
                                    </span>
                                </div>
                                <div className="absolute top-4 right-4 rounded-full p-2 bg-white">
                                    <product.icon className="w-5 h-5 text-blue-600" />
                                </div>
                            </div>

                            <div className="p-6 ">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-xl font-semibold text-gray-900">{product.name}</h3>
                                    <div className="flex items-center space-x-1">
                                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                        <span className="text-sm text-gray-600">{product.rating}</span>
                                    </div>
                                </div>

                                <p className="text-gray-600 mb-4 leading-relaxed">{product.description}</p>

                                <div className="space-y-2 mb-4">
                                    {product.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-center text-sm text-gray-700">
                                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                                            {feature}
                                        </div>
                                    ))}
                                </div>

                                <div className="border-t border-gray-100 pt-4 ">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <div className="text-lg font-semibold text-gray-900">{product.price}</div>
                                            <div className="text-sm text-gray-600">{product.users} active users</div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => window.open(product.link, '_blank')}
                                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center group">
                                        Learn More
                                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Custom Solutions Available</h3>
                    <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                        Don't see exactly what you need? We specialize in creating custom solutions tailored to your specific
                        requirements and workflow. Let's discuss how we can build the perfect solution for your finance office.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                            Request Custom Solution
                        </button>
                        <button className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors">
                            Schedule Demo
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Products;